import { useEffect, useRef, Fragment } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useActiveApplicationIdentifier } from '@trinity/chat-components/containers/UserApplications/useActiveApplicationIdentifier.js';
import { useActiveBrandIdentifier } from '@trinity/chat-components/containers/UserBrands/useActiveBrandIdentifier.js';
import { useAppReadyState, useAuthChangeEffect, useHydrateEffect } from '@/containers/AppReadyState';
import { useFetchApp, useInitSentry } from '@/containers/AppStateEffect/hooks';
import { useSelectHomePageLocation } from '@/store/selectors';
import { useValidSessionChecker } from './hooks/useValidSessionChecker';

const HOME_DESTINATION_PREFIXES = ['/chat', '/main', '/workspaces', '/page-home', '/pages'];

type Props = {
  children: React.ReactNode;
};

export const AppStateEffectContainer = (props: Props) => {
  const navigate = useNavigate();
  const location = useLocation();
  const app = useAppReadyState();
  const brandIdentifier = useActiveBrandIdentifier();
  const activeApplicationIdentifier = useActiveApplicationIdentifier();
  const homeRoute = useSelectHomePageLocation();

  const fetchApp = useFetchApp();
  const prevAppIdRef = useRef(activeApplicationIdentifier);

  const initSentry = useInitSentry();

  useHydrateEffect(() => {
    initSentry();
  });

  useAuthChangeEffect(auth => {
    if (!auth) {
      navigate('/logout');
    }
  });

  // When the active application changes, immediately navigate to /home so the
  // user sees the loading spinner instead of a stale page flickering.
  useEffect(() => {
    if (prevAppIdRef.current !== activeApplicationIdentifier && app.initialized) {
      const isAtHomeDestination = HOME_DESTINATION_PREFIXES.some(prefix =>
        location.pathname.startsWith(prefix),
      );
      if (isAtHomeDestination) {
        navigate('/home', { replace: true });
      }
    }
    prevAppIdRef.current = activeApplicationIdentifier;
  }, [activeApplicationIdentifier, app.initialized, location.pathname, navigate]);

  useEffect(() => {
    if (app.authenticated && brandIdentifier && activeApplicationIdentifier) {
      fetchApp(brandIdentifier, activeApplicationIdentifier);
    }
  }, [
    app.authenticated,
    brandIdentifier,
    activeApplicationIdentifier,
    fetchApp,
  ]);

  // When fetchApp resolves and the user is still on /home (the loading screen),
  // redirect to the resolved homeRoute. We only redirect from /home so that
  // explicit user navigation (e.g. clicking "Insights Chat" → /chat) is not
  // overridden.
  useEffect(() => {
    if (!app.initialized || !homeRoute) return;
    if (location.pathname === '/home') {
      navigate(homeRoute, { replace: true });
    }
  }, [homeRoute, app.initialized, location.pathname, navigate]);

  useValidSessionChecker({
    enabled: app.initialized && app.authenticated,
  });

  return (
    <Fragment>
      {props.children}
    </Fragment>
  );
};

AppStateEffectContainer.displayName = 'AppStateEffectContainer';